import {GET_LIST, GET_ONE, GET_MANY, GET_MANY_REFERENCE, CREATE, UPDATE, DELETE} from 'react-admin';

import {RESOURCES} from '~constants/config';
import {
    formatCreateResource,
    parseResponse,
    parseResourceDetail,
    formatResponseData,
    formatShowAccion,
    formatAccionFormData,
    formatTerritorioFormData,
    formatLayerFormData
} from '~utils/mappers';
import axios from 'axios';
import {getApiHeaders} from 'utils/helpers';
import {transformQueryParams, filterUnfinishedDateParams} from '~utils/object';

import api, {apiSetup} from '../api';
import {DELETE_MANY} from 'ra-core';
import HttpError from 'ra-core/lib/util/HttpError';

apiSetup();

function extractErrorMessage(data) {
    if (!data) return null;
    if (typeof data === 'string') return data;
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.error === 'string') return data.error;
    if (typeof data.message === 'string') return data.message;
    const messages = [];
    for (const key of Object.keys(data)) {
        const val = data[key];
        if (Array.isArray(val)) {
            messages.push(`${key}: ${val.join(', ')}`);
        } else if (typeof val === 'string') {
            messages.push(`${key}: ${val}`);
        }
    }
    return messages.length > 0 ? messages.join('\n') : null;
}

function throwHttpError(response) {
    const status = response.status || 500;
    const data = response.data;
    const message = extractErrorMessage(data) || response.problem || response.statusText || 'Error desconocido';
    throw new HttpError(message, status, data);
}

// Plain JSON catalogs created/updated through formatCreateResource.
const CATALOG_RESOURCES = [
    'priorizaciones', 'disclaimers', 'studies', 'scopes', 'terms', 'sources',
    'financiadores-estudios', 'types', 'themes', 'subthemes', 'chart-categories', 'subtheme-categories',
    'metrics', 'metric-values'
];

export const PUBLISH = 'publish';
export const UNPUBLISH = 'unpublish';
export const PROMOTE = 'promote';

export default (type, resource, params) => {
    switch (type) {
        case GET_LIST: {
            const shared_params = {
                ...filterUnfinishedDateParams(transformQueryParams(params.filter)),
                page: params.pagination.page,
                page_size: params.pagination.perPage,
            };
            const getParams = {
                ...shared_params,
                sort: params.sort.field,
                order: params.sort.order
            };
            return api
                .get(`/${RESOURCES[resource]}`, getParams)
                .then(response => {
                    if (response.ok) {
                        console.log({response});
                        if (Array.isArray(response.data)) {
                            const formatted = formatResponseData[resource](response.data);
                            return { data: formatted, total: formatted.length };
                        }
                        response.data.page = formatResponseData[resource](response.data.page);
                        const parsedResponse = parseResponse(response);
                        return parsedResponse;
                    }
                    throwHttpError(response);
                });
        }
        case GET_ONE: {
            return api.get(`/${RESOURCES[resource]}/${params.id}`).then(response => {
                if (response.ok) {
                    let parser = parseResourceDetail[resource];
                    if (resource == 'actions' && window.location.href.includes('/show')) {
                        parser = formatShowAccion;
                    }
                    if (!parser) {
                        parser = (data) => data;
                    }
                    return {data: parser(response.data)};
                }
                throwHttpError(response);
            });
        }
        case GET_MANY: {
            const ids = params.ids;
            return Promise.all(
                ids.map(id => api.get(`/${RESOURCES[resource]}/${id}`).then(response => {
                    if (response.ok) {
                        return response.data;
                    }
                    throwHttpError(response);
                }))
            ).then(data => ({data}));
        }
        case GET_MANY_REFERENCE: {
            const getParams = {
                ...filterUnfinishedDateParams(transformQueryParams(params.filter || {})),
                page: params.pagination.page,
                page_size: params.pagination.perPage,
                sort: params.sort.field,
                order: params.sort.order,
                [params.target]: params.id
            };
            return api
                .get(`/${RESOURCES[resource]}`, getParams)
                .then(response => {
                    if (response.ok) {
                        if (Array.isArray(response.data)) {
                            return {data: response.data, total: response.data.length};
                        }
                        const parsedResponse = parseResponse(response);
                        return parsedResponse;
                    }
                    throwHttpError(response);
                });
        }
        case CREATE: {
            const api_url = `${RESOURCES[resource]}/`;

            if (resource == 'actions') {
                const formData = createAccionFormData(params.data);
                let baseUrl = process.env.REACT_APP_BASE_URL;
                let headers = getApiHeaders();
                return axios.post(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        const data = response.data;
                        setTimeout(function () {
                            location.reload()
                        }, 2000);
                        return {data: {...data, id: data.code}};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            } else if (resource == 'draft-actions') {
                const formData = createAccionFormData(params.data);
                let baseUrl = process.env.REACT_APP_BASE_URL;
                let headers = getApiHeaders();
                return axios.post(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        const data = response.data;
                        return {data: {...data, id: data.uuid}};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            } else if (resource == 'territories') {
                const formData = createTerritorioFormData(params.data);
                let baseUrl = process.env.REACT_APP_BASE_URL;
                let headers = getApiHeaders();
                return axios.post(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        setTimeout(function () {
                            location.reload()
                        }, 2000);
                        return {data: response.data};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            } else if (resource == 'paises') {
                const formData = buildPaisFormData(params.data);
                const baseUrl = process.env.REACT_APP_BASE_URL;
                const headers = getApiHeaders();
                return axios.post(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        return {data: response.data};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            } else if (resource == 'layers') {
                const {name, files} = formatLayerFormData(params.data);
                const baseUrl = process.env.REACT_APP_BASE_URL;
                const headers = getApiHeaders();
                const url = `${baseUrl}${api_url}`;

                const buildFormData = replace => {
                    const formData = new FormData();
                    formData.append('name', name);
                    // Repeated 'files' parts so DRF request.FILES.getlist('files') reads them.
                    files.forEach(file => formData.append('files', file));
                    if (replace) {
                        formData.append('replace', 'true');
                    }
                    return formData;
                };

                const postLayer = replace =>
                    axios.post(url, buildFormData(replace), {headers, timeout: 0})
                        .then(response => {
                            if (response.status >= 200 && response.status < 300) {
                                return {data: response.data};
                            }
                            throwHttpError(response);
                        });

                // First attempt without replace. If the name already exists the backend
                // returns 409; ask the user whether to overwrite, and only then re-POST
                // with replace=true. Any other error propagates as usual.
                return postLayer(false).catch(error => {
                    const resp = error.response;
                    if (resp && resp.status === 409 && resp.data && resp.data.code === 'name_exists') {
                        const confirmed = window.confirm(
                            `Ya existe una capa llamada "${name}".\n\n` +
                            'Aceptar: reemplazar los datos de la capa existente.\n' +
                            'Cancelar: abortar.'
                        );
                        if (!confirmed) {
                            throw new HttpError('Carga cancelada.', 409, resp.data);
                        }
                        return postLayer(true).catch(replaceError => {
                            throwHttpError(replaceError.response
                                || {status: 500, data: {error: replaceError.message}});
                        });
                    }
                    throwHttpError(resp || {status: 500, data: {error: error.message}});
                });
            } else {
                const creationParams = formatCreateResource[resource](params.data);
                return api.post(`/${api_url}`, creationParams).then(response => {
                    if (response.ok) {
                        return {data: response.data};
                    }
                    throwHttpError(response);
                });
            }
        }
        case PUBLISH: {
            return api.patch(`/${RESOURCES[resource]}/${params.id}/publish/`);
        }
        case UNPUBLISH: {
            return api.patch(`/${RESOURCES[resource]}/${params.id}/unpublish/`);
        }
        case PROMOTE: {
            return api.post(`/${RESOURCES[resource]}/${params.id}/promote/`).then(response => {
                if (response.ok) {
                    return {data: response.data};
                }
                throwHttpError(response);
            });
        }
        case DELETE: {
            return doDelete(resource, params.id);
        }
        case UPDATE: {
            const api_url = `${RESOURCES[resource]}/${params.id}/`;
            if (resource == 'paises') {
                const formData = buildPaisFormData(params.data);
                const baseUrl = process.env.REACT_APP_BASE_URL;
                const headers = getApiHeaders();
                return axios.put(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                    if (response.status >= 200 && response.status < 300) {
                        return {data: response.data};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            }
            if (resource == 'etapas' || CATALOG_RESOURCES.includes(resource)) {
                const updateParams = formatCreateResource[resource](params.data);
                return api.put(`/${api_url}`, updateParams).then(response => {
                    if (response.ok) {
                        return {data: response.data};
                    }
                    throwHttpError(response);
                });
            }
            if (resource == 'draft-actions') {
                const draftFormData = createAccionFormData(params.data);
                const draftBaseUrl = process.env.REACT_APP_BASE_URL;
                const draftHeaders = getApiHeaders();
                return axios.put(`${draftBaseUrl}${api_url}`, draftFormData, {headers: draftHeaders}).then(response => {
                    const {data, status} = response;
                    if (status == 200) {
                        return {data: {...data, id: data.uuid}};
                    }
                    throwHttpError(response);
                }).catch(error => {
                    throwHttpError(error.response || {status: 500, data: {error: error.message}});
                });
            }
            let formData;
            if (resource == 'actions') {
                formData = createAccionFormData(params.data);

            } else {
                formData = createTerritorioFormData(params.data);
            }
            let baseUrl = process.env.REACT_APP_BASE_URL;
            let headers = getApiHeaders();
            return axios.put(`${baseUrl}${api_url}`, formData, {headers: headers}).then(response => {
                const {data, status} = response
                if (status == 200) {
                    return {data: {...data, id: data.code}};
                }
                throwHttpError(response);
            }).catch(error => {
                throwHttpError(error.response || {status: 500, data: {error: error.message}});
            });
        }
        case DELETE_MANY: {
            deleteMany(resource, params.ids[0], params.ids);
            return {data: [...params.ids]};
        }
        default:
            throw new Error('provider.invalidAction');
    }
};

function buildPaisFormData(data) {
    const formData = new FormData();
    if (data.name !== undefined && data.name !== null) {
        formData.append('name', data.name);
    }
    // Empty clears the ISO code; the API fills it back for the countries it knows by name.
    formData.append('code', data.code || '');
    if (data.flag && data.flag.rawFile) {
        formData.append('flag', data.flag.rawFile);
    }
    return formData;
}

function mapArrayToFormData(formData, key, array) {
    array.forEach((item, idx) => {
        mapValueToFormData(formData, `${key}[${idx}]`, item);
    });
    return formData;
}

function mapValueToFormData(formData, key, value) {
    if (value instanceof Object) {
        if (value instanceof Date) {
            formData.append(key, value.toISOString());
        } else if (value instanceof File) {
            formData.append(key, value);
        } else if (value instanceof Array) {
            mapArrayToFormData(formData, key, value);
        } else {
            mapObjectToFormData(formData, key, value);
        }
    } else {
        if (value !== undefined && value !== null) {
            formData.append(key, value);
        }
    }
    return formData;
}

function mapObjectToFormData(formData, key, obj) {
    Object.keys(obj).forEach(subKey => {
        mapValueToFormData(formData, `${key}${!key || key.endsWith(']') ? '' : '.'}${subKey}`, obj[subKey]);
    });
    return formData;
}

function createAccionFormData(params) {
    let formData = new FormData();
    let data = formatAccionFormData(params);
    mapValueToFormData(formData, '', data);
    return formData;
}

function createTerritorioFormData(params) {
    let formData = new FormData();
    let data = formatTerritorioFormData(params);
    mapValueToFormData(formData, '', data);
    return formData;

}

function deleteMany(resource, id, idList) {
    let res = doDelete(resource, id);
    res.then(r => {
        let idx = idList.indexOf(id);
        if (idx < (idList.length - 1)) {
            deleteMany(resource, idList[idx + 1], idList);
        }
    });
}

function doDelete(resource, id) {
    return api.delete(`/${RESOURCES[resource]}/${id}/`).then(response => {
        if (response.ok) {
            return {data: {id: id}};
        }
        throwHttpError(response);
    });
}
